// src/components/ReturnPolicy.js
import React from "react";
import { Container, Box, Typography, Paper, Button } from "@mui/material";
import { ArrowBack } from "@mui/icons-material";
import { MERCHANT_LEGAL } from "../config/merchantLegalConfig";
import PolicyContactSection from "./PolicyContactSection";

const ReturnPolicy = ({ onBack, primaryBlue, glassStyles }) => {
  const defaultPrimaryBlue = primaryBlue || {
    50: "#F0F8FF",
    100: "#E6F0FA",
    500: "#1E40AF",
    600: "#002366",
    700: "#001A4D",
    800: "#0B1C48",
  };

  const defaultGlassStyles = glassStyles || {
    background: "rgba(255, 255, 255, 0.25)",
    backdropFilter: "blur(20px)",
    border: "1px solid rgba(255, 255, 255, 0.18)",
    borderRadius: "16px",
    boxShadow: "0 8px 32px rgba(11, 28, 72, 0.37)",
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: `linear-gradient(135deg, ${defaultPrimaryBlue[50]} 0%, ${defaultPrimaryBlue[100]} 100%)`,
        py: 4,
      }}
    >
      <Container maxWidth="md">
        <Button
          startIcon={<ArrowBack />}
          onClick={onBack}
          sx={{
            mb: 3,
            color: defaultPrimaryBlue[700],
            fontWeight: 600,
          }}
        >
          Back
        </Button>

        <Paper
          elevation={0}
          sx={{
            ...defaultGlassStyles,
            p: { xs: 3, md: 5 },
            bgcolor: "rgba(255, 255, 255, 0.9)",
          }}
        >
          <Typography
            variant="h3"
            gutterBottom
            sx={{
              fontWeight: 700,
              color: defaultPrimaryBlue[700],
              mb: 2,
            }}
          >
            Return & Refund Policy
          </Typography>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
            Last Updated: January 2025
          </Typography>

          <Box sx={{ "& > *": { mb: 3 } }}>
            <Box>
              <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, color: defaultPrimaryBlue[700] }}>
                1. Overview
              </Typography>
              <Typography variant="body1" paragraph>
                At InfinityWhale, we want you to be completely satisfied with your purchase. If you're not happy with your order, we offer a 7-day return policy for eligible products. This policy outlines the terms and conditions for returns, exchanges, and refunds.
              </Typography>
              <Typography variant="body1" paragraph>
                Please read this policy carefully before making a purchase. By placing an order, you agree to the terms outlined in this Return & Refund Policy.
              </Typography>
            </Box>

            <Box>
              <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, color: defaultPrimaryBlue[700] }}>
                2. Return Eligibility
              </Typography>
              <Typography variant="body1" paragraph>
                <strong>2.1 Return Window:</strong> You may return eligible products within 7 days of delivery. The return period starts from the date you receive the product.
              </Typography>
              <Typography variant="body1" paragraph>
                <strong>2.2 Eligible Products:</strong> To be eligible for a return, products must meet the following conditions:
              </Typography>
              <Typography variant="body1" component="div" sx={{ pl: 2 }}>
                • Product must be unused and in the same condition as received<br />
                • Product must be in original packaging with all tags and labels intact<br />
                • Product must include all accessories, manuals, and documentation<br />
                • Product must not show signs of wear, damage, or alteration<br />
                • Original invoice or proof of purchase must be provided
              </Typography>
            </Box>

            <Box>
              <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, color: defaultPrimaryBlue[700] }}>
                3. Non-Returnable Items
              </Typography>
              <Typography variant="body1" paragraph>
                The following items are not eligible for return:
              </Typography>
              <Typography variant="body1" component="div" sx={{ pl: 2 }}>
                • <strong>Subscriptions:</strong> Activation fees and subscriptions are non-refundable once activated<br />
                • <strong>Digital Products:</strong> Downloadable products, gift cards, and vouchers<br />
                • <strong>Personal Care Items:</strong> Products for hygiene reasons (cosmetics, undergarments, etc.)<br />
                • <strong>Perishable Goods:</strong> Food items, flowers, or other perishables<br />
                • <strong>Custom/Personalized Items:</strong> Products made to order or customized for you<br />
                • <strong>Sale/Clearance Items:</strong> Products marked as final sale or clearance<br />
                • <strong>Opened/Used Products:</strong> Items that have been opened, used, or damaged by the customer
              </Typography>
            </Box>

            <Box>
              <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, color: defaultPrimaryBlue[700] }}>
                4. How to Initiate a Return
              </Typography>
              <Typography variant="body1" paragraph>
                To initiate a return, please follow these steps:
              </Typography>
              <Typography variant="body1" component="div" sx={{ pl: 2 }}>
                <strong>Step 1:</strong> Contact our customer support within 7 days of delivery<br />
                • Email: {MERCHANT_LEGAL.supportEmail}<br />
                • Phone: {MERCHANT_LEGAL.supportPhone}<br />
                • Business Hours: Mon-Sat, 9 AM - 6 PM IST<br />
                <br />
                <strong>Step 2:</strong> Provide the following information:<br />
                • Order number<br />
                • Product name and details<br />
                • Reason for return<br />
                • Photos of the product (if damaged or defective)<br />
                <br />
                <strong>Step 3:</strong> Wait for return authorization<br />
                • Our team will review your request within 24-48 hours<br />
                • You will receive a Return Authorization (RA) number if approved<br />
                <br />
                <strong>Step 4:</strong> Ship the product back<br />
                • Pack the product securely in original packaging<br />
                • Include the RA number and invoice<br />
                • Ship to the address provided by our team<br />
                <br />
                <strong>Step 5:</strong> Track your return<br />
                • Keep the shipping receipt and tracking number<br />
                • We will notify you once we receive and inspect the product
              </Typography>
            </Box>

            <Box>
              <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, color: defaultPrimaryBlue[700] }}>
                5. Return Shipping Costs
              </Typography>
              <Typography variant="body1" paragraph>
                <strong>5.1 Customer Responsibility:</strong> For returns due to change of mind or incorrect ordering, the customer is responsible for return shipping costs.
              </Typography>
              <Typography variant="body1" paragraph>
                <strong>5.2 Our Responsibility:</strong> We will cover return shipping costs if:
              </Typography>
              <Typography variant="body1" component="div" sx={{ pl: 2 }}>
                • Product is defective or damaged upon arrival<br />
                • Wrong product was shipped<br />
                • Product does not match the description<br />
                • Product has manufacturing defects
              </Typography>
              <Typography variant="body1" paragraph sx={{ mt: 2 }}>
                <strong>5.3 Prepaid Returns:</strong> In cases where we cover shipping, we will provide a prepaid return label or arrange pickup at no cost to you.
              </Typography>
            </Box>

            <Box>
              <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, color: defaultPrimaryBlue[700] }}>
                6. Inspection and Processing
              </Typography>
              <Typography variant="body1" paragraph>
                <strong>6.1 Inspection Timeline:</strong> Once we receive your returned product, we will inspect it within 3-5 business days.
              </Typography>
              <Typography variant="body1" paragraph>
                <strong>6.2 Inspection Criteria:</strong> We will verify that:
              </Typography>
              <Typography variant="body1" component="div" sx={{ pl: 2 }}>
                • Product meets return eligibility requirements<br />
                • Product is in original condition and packaging<br />
                • All accessories and documentation are included<br />
                • Product has not been used or damaged
              </Typography>
              <Typography variant="body1" paragraph sx={{ mt: 2 }}>
                <strong>6.3 Approval or Rejection:</strong> After inspection, we will notify you via email whether your return is approved or rejected. If rejected, we will explain the reason and may offer alternatives.
              </Typography>
            </Box>

            <Box>
              <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, color: defaultPrimaryBlue[700] }}>
                7. Refund Process
              </Typography>
              <Typography variant="body1" paragraph>
                <strong>7.1 Refund Timeline:</strong> If your return is approved, we will process your refund within 5-7 business days.
              </Typography>
              <Typography variant="body1" paragraph>
                <strong>7.2 Refund Method:</strong> Refunds will be issued to the original payment method:
              </Typography>
              <Typography variant="body1" component="div" sx={{ pl: 2 }}>
                • <strong>UPI/Bank Transfer:</strong> 3-5 business days<br />
                • <strong>Credit/Debit Card:</strong> 5-10 business days (depending on your bank)<br />
                • <strong>Digital Wallet:</strong> 2-3 business days
              </Typography>
              <Typography variant="body1" paragraph sx={{ mt: 2 }}>
                <strong>7.3 Refund Amount:</strong> The refund will include:
              </Typography>
              <Typography variant="body1" component="div" sx={{ pl: 2 }}>
                • Product price (including GST)<br />
                • Shipping charges (if we were at fault)<br />
                • Any applicable discounts or promotions
              </Typography>
              <Typography variant="body1" paragraph sx={{ mt: 2 }}>
                <strong>7.4 Deductions:</strong> The following may be deducted from your refund:
              </Typography>
              <Typography variant="body1" component="div" sx={{ pl: 2 }}>
                • Return shipping costs (if customer's responsibility)<br />
                • Restocking fee (up to 10% for certain products)<br />
                • Depreciation for used or damaged products
              </Typography>
              <Typography variant="body1" paragraph sx={{ mt: 2 }}>
                <strong>7.5 Partial Refunds:</strong> Partial refunds may be issued if:
              </Typography>
              <Typography variant="body1" component="div" sx={{ pl: 2 }}>
                • Product shows signs of use or wear<br />
                • Product is missing accessories or packaging<br />
                • Product is returned after the 7-day window (at our discretion)
              </Typography>
            </Box>

            <Box>
              <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, color: defaultPrimaryBlue[700] }}>
                8. Exchanges
              </Typography>
              <Typography variant="body1" paragraph>
                <strong>8.1 Exchange Policy:</strong> We offer exchanges for defective or damaged products, or if you received the wrong item.
              </Typography>
              <Typography variant="body1" paragraph>
                <strong>8.2 Size/Color Exchanges:</strong> If you need a different size or color, please initiate a return and place a new order. This ensures faster processing.
              </Typography>
              <Typography variant="body1" paragraph>
                <strong>8.3 Exchange Process:</strong> To request an exchange:
              </Typography>
              <Typography variant="body1" component="div" sx={{ pl: 2 }}>
                • Contact customer support with your order details<br />
                • Specify the product you want to exchange<br />
                • Return the original product following our return process<br />
                • We will ship the replacement once we receive and approve the return
              </Typography>
              <Typography variant="body1" paragraph sx={{ mt: 2 }}>
                <strong>8.4 Exchange Shipping:</strong> We cover shipping costs for exchanges due to defects or our errors. For other exchanges, standard shipping charges apply.
              </Typography>
            </Box>

            <Box>
              <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, color: defaultPrimaryBlue[700] }}>
                9. Damaged or Defective Products
              </Typography>
              <Typography variant="body1" paragraph>
                <strong>9.1 Reporting Damage:</strong> If you receive a damaged or defective product, please notify us within 48 hours of delivery with:
              </Typography>
              <Typography variant="body1" component="div" sx={{ pl: 2 }}>
                • Clear photos of the damage<br />
                • Photos of the packaging<br />
                • Order number and product details<br />
                • Description of the issue
              </Typography>
              <Typography variant="body1" paragraph sx={{ mt: 2 }}>
                <strong>9.2 Resolution Options:</strong> For damaged or defective products, we offer:
              </Typography>
              <Typography variant="body1" component="div" sx={{ pl: 2 }}>
                • Full refund (including shipping)<br />
                • Free replacement<br />
                • Repair or partial refund (if applicable)
              </Typography>
              <Typography variant="body1" paragraph sx={{ mt: 2 }}>
                <strong>9.3 No-Cost Returns:</strong> Returns for damaged or defective products are free. We will arrange pickup or provide a prepaid return label.
              </Typography>
            </Box>

            <Box>
              <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, color: defaultPrimaryBlue[700] }}>
                10. Wrong Product Delivered
              </Typography>
              <Typography variant="body1" paragraph>
                If you receive the wrong product:
              </Typography>
              <Typography variant="body1" component="div" sx={{ pl: 2 }}>
                • Contact us immediately with order details and photos<br />
                • We will arrange pickup of the wrong product at no cost<br />
                • We will ship the correct product immediately<br />
                • You will receive a full refund if the correct product is unavailable
              </Typography>
            </Box>

            <Box>
              <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, color: defaultPrimaryBlue[700] }}>
                11. Referral Program and Returns
              </Typography>
              <Typography variant="body1" paragraph>
                <strong>11.1 Impact on Referral Points:</strong> If a product purchased through a referral is returned:
              </Typography>
              <Typography variant="body1" component="div" sx={{ pl: 2 }}>
                • Referral points credited for that purchase will be reversed<br />
                • This applies to all levels of the referral chain<br />
                • Points will be deducted from referrer accounts
              </Typography>
              <Typography variant="body1" paragraph sx={{ mt: 2 }}>
                <strong>11.2 Subscriptions:</strong> Activation fees and subscriptions are non-refundable. Returns do not affect Ultra Customer status once activated.
              </Typography>
            </Box>

            <Box>
              <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, color: defaultPrimaryBlue[700] }}>
                12. Late or Missing Refunds
              </Typography>
              <Typography variant="body1" paragraph>
                If you haven't received your refund within the stated timeline:
              </Typography>
              <Typography variant="body1" component="div" sx={{ pl: 2 }}>
                <strong>Step 1:</strong> Check your bank account or payment method again<br />
                <strong>Step 2:</strong> Contact your bank or payment provider (processing may take time)<br />
                <strong>Step 3:</strong> Contact us at {MERCHANT_LEGAL.supportEmail} with your order number<br />
                <strong>Step 4:</strong> We will investigate and provide a resolution within 48 hours
              </Typography>
            </Box>

            <Box>
              <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, color: defaultPrimaryBlue[700] }}>
                13. Bulk Orders and Special Cases
              </Typography>
              <Typography variant="body1" paragraph>
                <strong>13.1 Bulk Orders:</strong> Returns for bulk orders (10+ units) may have different terms. Please contact us before placing bulk orders.
              </Typography>
              <Typography variant="body1" paragraph>
                <strong>13.2 Corporate Orders:</strong> Corporate or business orders may have customized return policies as per the agreement.
              </Typography>
            </Box>

            <Box>
              <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, color: defaultPrimaryBlue[700] }}>
                14. Consumer Rights
              </Typography>
              <Typography variant="body1" paragraph>
                This policy does not affect your statutory rights under the Consumer Protection Act, 2019. You have the right to:
              </Typography>
              <Typography variant="body1" component="div" sx={{ pl: 2 }}>
                • Receive products that match their description<br />
                • Return defective or unsafe products<br />
                • Seek compensation for damages<br />
                • File complaints with consumer forums
              </Typography>
            </Box>

            <Box>
              <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, color: defaultPrimaryBlue[700] }}>
                15. Policy Updates
              </Typography>
              <Typography variant="body1" paragraph>
                We reserve the right to modify this Return & Refund Policy at any time. Changes will be effective immediately upon posting on our website. Your continued use of our services after changes constitutes acceptance of the updated policy.
              </Typography>
            </Box>

            <Box>
              <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, color: defaultPrimaryBlue[700] }}>
                16. Contact Us
              </Typography>
              <PolicyContactSection intro="For questions about returns or refunds, please contact us:" />
            </Box>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default ReturnPolicy;
